import { FullScreenLayout } from 'ui/fullScreen/FullScreenLayout';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { pxToRem } from 'utils/convert';
import { spacing } from 'ui/theme';
import { selectIsWhatsAppNewCharLimitsFeatureFlagOn } from 'core/userContext';
import { TemplateType } from 'constants/templateTypes';
import { WarningBanner } from 'ui/banner';
import { PreviewPane } from './preview/PreviewPane';
import { DataPane } from '../common/PreviewWithDataDataPane';
import { SettingsDrawer } from '../common/SettingsDrawer';
import {
  EditorContentContainer,
  LayoutContentContainerFullWidth,
  PreviewWithErrorContainer,
} from '../common';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { ViewFooter } from '../common/ViewFooter';
import { ViewHeader } from '../common/ViewHeader';
import { useTemplateQueryContext } from '../context/TemplateQueryContext';
import { PreviewContentContainer } from '../common/PreviewContentContainer';
import { useWhatsAppPreviewContext } from './context/WhatsAppPreviewContext';
import { PreviewWithDataError } from '../common/PreviewWithDataError';
import {
  MAX_MESSAGE_LENGTH_BUSINESS,
  MAX_MESSAGE_LENGTH_USER,
  NEW_MAX_MESSAGE_LENGTH_BUSINESS,
} from './constants';

export const TEMPLATE_EDITOR_WHATSAPP_W_DATA = 'template-editor-whats-app-preview-w-data';

const StyledWarningBanner = styled(WarningBanner)`
  align-items: baseline;

  @media screen and (max-width: ${pxToRem(893)}) {
    width: 100%;
  }
  margin-bottom: ${spacing.half};
`;

export const PreviewWithDataCore = () => {
  const isNewCharLimit = useSelector(selectIsWhatsAppNewCharLimitsFeatureFlagOn);
  const maxBusinessCharLimit = isNewCharLimit
    ? NEW_MAX_MESSAGE_LENGTH_BUSINESS
    : MAX_MESSAGE_LENGTH_BUSINESS;
  const { onRefreshPreview, previewError, whatsAppMessage } = useWhatsAppPreviewContext();
  const { liveTemplate } = useEditableFieldsContext();
  const { templateType } = liveTemplate;

  const charLimit =
    templateType === TemplateType.BusinessInitiated
      ? maxBusinessCharLimit
      : MAX_MESSAGE_LENGTH_USER;
  return (
    <EditorContentContainer data-test={TEMPLATE_EDITOR_WHATSAPP_W_DATA}>
      <DataPane onRefreshPreview={onRefreshPreview} />
      <PreviewWithErrorContainer>
        {previewError && <PreviewWithDataError previewError={previewError} />}
        {(whatsAppMessage?.length ?? 0) > charLimit && (
          <StyledWarningBanner>
            {isNewCharLimit ? (
              <>
                The message <b>body</b> with sample content exceeds Meta’s 550-character limit. This
                template may be rejected. Please shorten the message to comply with submission
                guidelines before submitting.
              </>
            ) : (
              <>
                Sample content in <b>body</b> exceeds the 1024-character limit. This template could
                be rejected by Meta. Shorten your message to ensure compliance with submission
                guidelines before submitting your template.
              </>
            )}
          </StyledWarningBanner>
        )}
        <PreviewPane />
      </PreviewWithErrorContainer>
    </EditorContentContainer>
  );
};

export const PreviewWithData = () => {
  const { onRefreshPreview } = useWhatsAppPreviewContext();
  const { refetch } = useTemplateQueryContext();

  return (
    <FullScreenLayout
      actionBarIsAnchored
      DrawerContent={
        <SettingsDrawer
          onSaveSuccess={() => {
            onRefreshPreview();
            refetch();
          }}
        />
      }
      HeaderContent={<ViewHeader />}
      FooterContent={<ViewFooter />}
    >
      <PreviewContentContainer data-test="template-editor-whats-app-preview">
        <LayoutContentContainerFullWidth>
          <PreviewWithDataCore />
        </LayoutContentContainerFullWidth>
      </PreviewContentContainer>
    </FullScreenLayout>
  );
};

export default PreviewWithData;
